import React, { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Text from '../global/Text';
import theme from '../../constants/theme';
import fontMaker from '../../utils/font';
import { NpsQuestion, NpsQuestionnaire } from '../../store/reducers/nps/type';
import NPSQuestionScore from './NPSQuestionScore';
import NPSQuestionChecklist from './NPSQuestionChecklist';
import NPSQuestionOpen from './NPSQuestionOpen';
import { buildNpsResponseDetails } from './npsResponseDetails';
import type { NpsResponseDetail, QuestionAnswers } from './npsResponseDetails';

// Flat list of all questions including conditional ones resolved from current answers
type FlatQuestion = {
  question: NpsQuestion;
  key: string;
  parentKey?: string;
  conditionalKey?: string;
};

type Props = {
  questionnaire: NpsQuestionnaire;
  isSubmitting: boolean;
  onSubmit: (responseDetails: NpsResponseDetail[]) => void;
  onDismiss: () => void;
};

function buildFlatQuestions(
  questions: NpsQuestion[],
  answers: QuestionAnswers,
  parentKey?: string,
  conditionalKey?: string
): FlatQuestion[] {
  const result: FlatQuestion[] = [];
  questions.forEach((q, idx) => {
    const key = parentKey ? `${parentKey}_CQ${String(idx).padStart(3, '0')}` : `Q${String(idx).padStart(3, '0')}`;
    result.push({ question: q, key, parentKey, conditionalKey });

    if (q.isConditional && q.conditionalQuestion?.conditions) {
      const answer = answers[key];
      if (answer?.type === 'score' && answer.value !== undefined) {
        const condKey = resolveScoreConditionKey(q, answer.value);
        if (condKey) {
          const children = q.conditionalQuestion.conditions[condKey] ?? [];
          result.push(...buildFlatQuestions(children, answers, key, condKey));
        }
      }
    }
  });
  return result;
}

function resolveScoreConditionKey(question: NpsQuestion, score: number): string | null {
  if (!question.conditionalQuestion?.conditions) return null;
  return (
    Object.keys(question.conditionalQuestion.conditions).find(condition => {
      if (condition.includes('-')) {
        const [start, end] = condition.split('-').map(Number);
        return start !== undefined && end !== undefined && score >= start && score <= end;
      }
      return Number(condition) === score;
    }) ?? null
  );
}

function validateAnswer(answers: QuestionAnswers, q: FlatQuestion): boolean {
  if (!q.question.answerRequired) return true;
  const ans = answers[q.key];
  if (!ans) return false;
  if (ans.type === 'score') return ans.value !== undefined && ans.value >= 0;
  if (ans.type === 'open') return ans.value.trim().length > 0;
  if (ans.type === 'checklist') return Object.values(ans.value).some(Boolean);
  return false;
}

const NPSQuestionPager = ({ questionnaire, isSubmitting, onSubmit, onDismiss }: Props) => {
  const { t } = useTranslation('common');
  const [answers, setAnswers] = useState<QuestionAnswers>({});
  const [currentPage, setCurrentPage] = useState(0);
  const [validationError, setValidationError] = useState(false);

  const flatQuestions = useMemo(
    () => buildFlatQuestions(questionnaire.questions, answers),
    [questionnaire.questions, answers]
  );

  const currentQuestion = flatQuestions[currentPage];
  const totalPages = flatQuestions.length;
  const isLastPage = currentPage === totalPages - 1;
  const isFirstPage = currentPage === 0;

  const updateAnswer = useCallback((key: string, value: QuestionAnswers[string]) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
    setValidationError(false);
  }, []);

  const goNext = useCallback(() => {
    if (!currentQuestion) return;
    if (!validateAnswer(answers, currentQuestion)) {
      setValidationError(true);
      return;
    }
    setValidationError(false);
    setCurrentPage(p => Math.min(p + 1, totalPages - 1));
  }, [answers, currentQuestion, totalPages]);

  const goPrev = useCallback(() => {
    setValidationError(false);
    setCurrentPage(p => Math.max(p - 1, 0));
  }, []);

  const handleSubmit = useCallback(() => {
    if (!currentQuestion) return;
    if (!validateAnswer(answers, currentQuestion)) {
      setValidationError(true);
      return;
    }
    onSubmit(buildNpsResponseDetails(questionnaire, answers));
  }, [answers, currentQuestion, onSubmit, questionnaire]);

  if (!currentQuestion) return null;

  const renderQuestion = () => {
    const { question, key } = currentQuestion;
    const ans = answers[key];

    if (question.type === 'score') {
      const scoreVal = ans?.type === 'score' ? ans.value : undefined;
      return (
        <NPSQuestionScore
          question={question}
          value={scoreVal}
          onChange={v => updateAnswer(key, { type: 'score', value: v })}
        />
      );
    }

    if (question.type === 'checklist') {
      const checkVal = ans?.type === 'checklist' ? ans.value : {};
      return (
        <NPSQuestionChecklist
          question={question}
          value={checkVal}
          onChange={v => updateAnswer(key, { type: 'checklist', value: v })}
        />
      );
    }

    if (question.type === 'open') {
      const openVal = ans?.type === 'open' ? ans.value : '';
      return (
        <NPSQuestionOpen
          question={question}
          value={openVal}
          onChange={v => updateAnswer(key, { type: 'open', value: v })}
        />
      );
    }

    return null;
  };

  return (
    <View style={styles.root}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        {renderQuestion()}
      </ScrollView>

      {validationError && <Text style={styles.errorText}>{t('nps.validationRequired')}</Text>}

      <View style={styles.footer}>
        <Text style={styles.pageIndicator}>
          {currentPage + 1} / {totalPages}
        </Text>
        <View style={styles.footerButtons}>
          {!isFirstPage && (
            <TouchableOpacity style={styles.backButton} onPress={goPrev}>
              <Text style={styles.backButtonText}>{t('back')}</Text>
            </TouchableOpacity>
          )}
          {!isLastPage && (
            <TouchableOpacity style={styles.nextButton} onPress={goNext}>
              <Text style={styles.nextButtonText}>{t('next')}</Text>
            </TouchableOpacity>
          )}
          {isLastPage && (
            <TouchableOpacity
              style={[styles.nextButton, isSubmitting && styles.disabledButton]}
              onPress={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <ActivityIndicator color="#fff" size="small" />
              ) : (
                <Text style={styles.nextButtonText}>{t('submit')}</Text>
              )}
            </TouchableOpacity>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: { flex: 1 },
  scroll: { flex: 1 },
  scrollContent: { flexGrow: 1, paddingBottom: 8 },
  errorText: {
    fontSize: 12,
    color: '#e53935',
    paddingHorizontal: 16,
    marginBottom: 4,
    ...fontMaker({ weight: 'Normal' })
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0'
  },
  pageIndicator: {
    flex: 1,
    fontSize: 12,
    color: theme.colors.gray,
    ...fontMaker({ weight: 'Normal' })
  },
  footerButtons: { flexDirection: 'row', gap: 8 },
  backButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8
  },
  backButtonText: {
    fontSize: 14,
    color: theme.colors.gray,
    ...fontMaker({ weight: 'SemiBold' })
  },
  nextButton: {
    paddingHorizontal: 20,
    paddingVertical: 8,
    backgroundColor: theme.colors.primary,
    borderRadius: 8,
    minWidth: 72,
    alignItems: 'center'
  },
  nextButtonText: {
    fontSize: 14,
    color: '#fff',
    ...fontMaker({ weight: 'SemiBold' })
  },
  disabledButton: { opacity: 0.6 }
});

export type { QuestionAnswers };
export default NPSQuestionPager;
